import {useCallback, useMemo} from 'react'
import {Pressable, View} from 'react-native'
import {useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {TIMELINE_SAVED_FEED} from '#/lib/constants'
import {type NavigationProp} from '#/lib/routes/types'
import {getLocalizedFeedName} from '#/lib/strings/feed-names'
import {type FeedSourceInfo} from '#/state/queries/feed'
import {useSession} from '#/state/session'
import {type RenderTabBarFnProps} from '#/view/com/pager/Pager'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'
import {TabBar} from '../pager/TabBar'
import {HomeHeaderLayout} from './HomeHeaderLayout'

export function HomeHeader(
  props: RenderTabBarFnProps & {
    testID?: string
    onPressSelected: () => void
    feeds: Pick<FeedSourceInfo, 'displayName' | 'uri'>[]
    feedScope: 'all' | 'community' | 'network'
    onFeedScopeChange: (scope: 'all' | 'community' | 'network') => void
  },
) {
  const t = useTheme()
  const {feeds, onSelect: onSelectProp} = props
  const {hasSession} = useSession()
  const {t: l, i18n} = useLingui()
  const navigation = useNavigation<NavigationProp>()

  const hasPinnedCustom = useMemo<boolean>(() => {
    if (!hasSession) return false
    return feeds.some(tab => {
      const isFollowing = tab.uri === TIMELINE_SAVED_FEED.value
      return !isFollowing
    })
  }, [feeds, hasSession])

  const items = useMemo(() => {
    const pinnedNames = feeds.map(f => getLocalizedFeedName(f, i18n))
    if (!hasPinnedCustom) {
      return pinnedNames.concat(l`Feeds ✨`)
    }
    return pinnedNames
  }, [i18n, l, hasPinnedCustom, feeds])

  const onPressFeedsLink = useCallback(() => {
    navigation.navigate('Feeds')
  }, [navigation])

  const onSelect = useCallback(
    (index: number) => {
      if (!hasPinnedCustom && index === items.length - 1) {
        onPressFeedsLink()
      } else if (onSelectProp) {
        onSelectProp(index)
      }
    },
    [items.length, onPressFeedsLink, onSelectProp, hasPinnedCustom],
  )

  return (
    <HomeHeaderLayout tabBarAnchor={props.tabBarAnchor}>
      <TabBar
        key={items.join(',')}
        onPressSelected={props.onPressSelected}
        selectedPage={props.selectedPage}
        onSelect={onSelect}
        testID={props.testID}
        items={items}
        dragProgress={props.dragProgress}
        dragState={props.dragState}
        transparent
      />

      <View style={[a.flex_row, a.justify_center, a.gap_sm, a.px_md, a.pb_sm]}>
        {(['all', 'community', 'network'] as const).map(scope => {
          const selected = props.feedScope === scope
          const label =
            scope === 'all'
              ? 'All'
              : scope === 'community'
                ? 'Community'
                : 'Network'

          return (
            <Pressable
              accessibilityRole="button"
              key={scope}
              onPress={() => props.onFeedScopeChange(scope)}
              style={[
                a.px_md,
                a.py_xs,
                a.rounded_full,
                selected ? t.atoms.bg_contrast_25 : t.atoms.bg_contrast_5,
              ]}>
              <Text
                style={[
                  a.text_sm,
                  selected
                    ? t.atoms.text_contrast_high
                    : t.atoms.text_contrast_medium,
                ]}>
                {label}
              </Text>
            </Pressable>
          )
        })}
      </View>
    </HomeHeaderLayout>
  )
}
